import { ReactNode } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  getOperatorBasePath,
  getOperatorSubtitle,
  getPrivateCustomersLabel,
  isDistributorRole
} from '../utils/operator';

interface CommercialLayoutProps {
  children: ReactNode;
  title: string;
}

const CommercialLayout = ({ children, title }: CommercialLayoutProps) => {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const role = auth.user?.role;
  const basePath = getOperatorBasePath(role);
  const isDistributor = isDistributorRole(role);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header con efecto de paralaje similar al AccountPage */}
      <div className="relative h-48 bg-black">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-black opacity-90"></div>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-[url('/bg-pattern.svg')] bg-center opacity-10"></div>
        </div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white">{title}</h1>
                <p className="text-gray-300 mt-1">{getOperatorSubtitle(role)}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-white text-sm font-medium">{auth.user?.name}</p>
                  <p className="text-gray-400 text-xs">{auth.user?.role}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-gray-700 border-2 border-gray-600 flex items-center justify-center text-white text-xl">
                  {auth.user?.name.charAt(0).toUpperCase()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Navegación superior */}
      <div className="sticky top-0 bg-white shadow-sm z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex space-x-4">
              <NavLink 
                to={basePath}
                end
                className={({ isActive }) => 
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-black border-b-2 border-black' : 'text-gray-600 hover:text-black'}`
                }
              >
                Dashboard
              </NavLink>
              {!isDistributor && (
                <NavLink 
                  to={`${basePath}/customers`} 
                  className={({ isActive }) => 
                    `px-3 py-2 text-sm font-medium ${isActive ? 'text-black border-b-2 border-black' : 'text-gray-600 hover:text-black'}`
                  }
                >
                  Clientes
                </NavLink>
              )}
              <NavLink 
                to={`${basePath}/private-customers`} 
                className={({ isActive }) => 
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-black border-b-2 border-black' : 'text-gray-600 hover:text-black'}`
                }
              >
                {getPrivateCustomersLabel(role)}
              </NavLink>
              <NavLink 
                to={`${basePath}/orders`} 
                className={({ isActive }) => 
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-black border-b-2 border-black' : 'text-gray-600 hover:text-black'}`
                }
              >
                Pedidos
              </NavLink>
              {isDistributor && (
                <NavLink
                  to="/account/drop-shipping-emails"
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium ${isActive ? 'text-black border-b-2 border-black' : 'text-gray-600 hover:text-black'}`
                  }
                >
                  Emails
                </NavLink>
              )}
            </div>
            
            <div className="flex items-center">
              <Link to="/" className="text-sm text-gray-600 hover:text-black mr-4">
                Volver a la tienda
              </Link>
              <button
                onClick={handleLogout}
                className="text-sm bg-black text-white px-4 py-1 rounded-full hover:bg-gray-800"
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contenido principal */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

export default CommercialLayout;




